import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { Button, LoadingState } from "~/common/components";
import BackArrow from "~/common/components/icons/BackArrow";
import { useListConsentDocumentsToSign } from "~/patients/hooks/useListConsentDocumentsToSign";
import { acknowledgeConsentFormSchema } from "~/patients/schemas/consentDocuments";
import { AcknowledgeConsentFormSchema } from "~/patients/types/consentDocuments";
import CheckBox from "../components/ui/CheckBox";
import { useGetCurrentPatient } from "../hooks";
import { useSignConsentDocuments } from "../hooks/useSignConsentDocuments";
import { ROUTES } from "../router";

export const AcknowledgeConsent = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: currentPatient } = useGetCurrentPatient();

  const organizationId =
    currentPatient?.organization_patients[0].organization_id;

  const organizationName =
    currentPatient?.organization_patients[0].organization.name;

  const { data: documentsToSign } =
    useListConsentDocumentsToSign(organizationId);

  const signConsentDocumentsMutation = useSignConsentDocuments();

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<AcknowledgeConsentFormSchema>({
    defaultValues: {
      consent: false,
    },
    resolver: zodResolver(acknowledgeConsentFormSchema),
  });

  useEffect(() => {
    const organizationHasNoConsentDocuments = documentsToSign?.length === 0;
    const userAlreadySigned = Boolean(
      currentPatient?.organization_patients[0].consent_signed_at,
    );

    if (userAlreadySigned || organizationHasNoConsentDocuments) {
      navigate(ROUTES.payment);
    }
  }, [currentPatient, documentsToSign, navigate]);

  if (!currentPatient || !documentsToSign) {
    return <LoadingState className="h-screen" />;
  }

  const onSubmit: SubmitHandler<AcknowledgeConsentFormSchema> = () => {
    signConsentDocumentsMutation.mutate(
      {
        organizationId,
        forms: documentsToSign.map((doc) => ({
          id: doc.id,
          file_id: doc.files[0].id,
        })),
      },
      {
        onSuccess: () => {
          toast.success("Consent forms signed successfully");
          void queryClient.invalidateQueries({
            queryKey: ["getCurrentPatient"],
          });
          navigate(ROUTES.payment);
        },
        onError: () => {
          toast.error("Failed to sign consent forms. Please try again.");
        },
      },
    );
  };

  return (
    <form
      className="flex min-h-screen flex-col gap-6 bg-primary-50 p-8 md:px-64"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Link
        to={ROUTES.leadIntakeIntroScreen}
        className="flex flex-row items-center gap-1"
      >
        <BackArrow className="size-5" />
        Home
      </Link>
      <p className="font-outfit text-3xl font-semibold">
        {`Consent forms for ${organizationName}`}
      </p>
      <p>
        To proceed with the consultation, you must review and agree to our terms
        of service. Each link will download a copy to your device.
      </p>
      <Controller
        name="consent"
        control={control}
        render={({ field }) => (
          <CheckBox
            id="consent"
            parentClassName="w-fit"
            onChange={(event) => {
              field.onChange(event.target.checked);
            }}
            onBlur={field.onBlur}
            value={field.value.toString()}
            checked={field.value}
            ref={field.ref}
            containerClassName="items-start"
            className="relative top-1"
          >
            I have read and agree to these documents
          </CheckBox>
        )}
      />
      {!!documentsToSign.length && (
        <ul className="list-inside list-disc pl-6">
          {documentsToSign.map((doc) => (
            <li key={doc.id}>
              <a href={doc.files[0].path} className="underline">
                {doc.name}
              </a>
            </li>
          ))}
        </ul>
      )}
      <Button
        isLoading={signConsentDocumentsMutation.isPending}
        disabled={!isValid || signConsentDocumentsMutation.isPending}
        type="submit"
        className="mt-auto"
      >
        Continue
      </Button>
    </form>
  );
};
